<template>
	<div @contextmenu="preventContextMenu">
		<div class="disconnected-alert" :class="{ visible: ss.runtime.networkError||(!realtime.connected&&ss.isAuthenticated&&realtime.firstConnectCompleted) }">{{ t('app.noServerConnection') }}</div>
		<div v-if="ss.isAuthenticated">
			<TopBar v-if="realtime.firstConnectCompleted&&dashboards.ready" />
			<div class="main-top-offset">
				<div class="background">
					<div class="logo">{{ ss.runtime.appName.toUpperCase() }}</div>
					<div class="loading" :style="{'opacity':realtime.firstConnectCompleted&&dashboards.ready?0:1}">{{ t('common.loading') }}</div>
				</div>
				<dashboard-container :style="{ 'display': ss.runtime.selectedSection.startsWith('DASHBOARD') ? 'block' : 'none' }" />
				<PhonebookContainer v-if="ss.runtime.selectedSection === 'PHONEBOOK'" />
			</div>
			<status-panel v-show="ss.settings.showStatusPanel"></status-panel>

		</div>
		<login v-if="showLogin"></login>
		<ErrorModal />
		<QuestionModal />
		<ToastHost />
		<GuestReadyHost />
		<SendSmsModal />
		<GuestInviteModal />
		<GuestInvitesDialog />
		<PhonebookGroupsModal />
		<SendGroupSmsModal />
		<EmployeeDataModal />
		<employee-redirect-modal />
		<SettingsModal />
		<CalendarModal />
		<CalendarEventModal />
		<SetPresenceModal ref="setPresence" />
		<PhonebookEdit />
		<RecordingsModal />
		<VoicemailsModal />
		<CdrModal />
		<PickAPhoneModal />
		<DashboardAutoCreateModal />
		<CdrNoteEditorModal />
		<div id="SipPhoneVideoContainer"></div>
		<VideoCallView />
	</div>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useEmployeesStore } from '@/stores/Employees'
import { useDashboardsStore } from '@/stores/Dashboards'

import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import DesktopNotification from '@/utilities/DesktopNotification'
import { useI18n } from 'vue-i18n'

import Login from '@/components/LoginDialog.vue'
import TopBar from '@/components/topbar/TopBar.vue'
import StatusPanel from '@/components/StatusPanel.vue'
import DashboardContainer from '@/components/dashboard/DashContainer.vue'
import PhonebookContainer from '@/components/phonebook/PhonebookContainer.vue'
import ErrorModal from '@/components/modals/ErrorModal.vue'
import QuestionModal from '@/components/modals/QuestionModal.vue'
import ToastHost from '@/components/ToastHost.vue'
import GuestReadyHost from '@/components/GuestReadyHost.vue'
import SetPresenceModal from '@/components/modals/SetPresenceModal.vue'
import VideoCallView from '@/components/video/VideoCallView.vue'
import Employee from './stores/Employee'
import { computed, onMounted, ref, defineAsyncComponent } from 'vue'

// Lazy load modal components that are not always visible
const SendSmsModal = defineAsyncComponent(() => import('@/components/modals/SendSmsModal.vue'))
const GuestInviteModal = defineAsyncComponent(() => import('@/components/modals/GuestInviteModal.vue'))
const GuestInvitesDialog = defineAsyncComponent(() => import('@/components/modals/GuestInvitesDialog.vue'))
const PhonebookGroupsModal = defineAsyncComponent(() => import('@/components/modals/PhonebookGroupsModal.vue'))
const SendGroupSmsModal = defineAsyncComponent(() => import('@/components/modals/SendGroupSmsModal.vue'))
const EmployeeDataModal = defineAsyncComponent(() => import('@/components/modals/EmployeeDataModal.vue'))
const EmployeeRedirectModal = defineAsyncComponent(() => import('@/components/modals/EmployeeRedirectModal.vue'))
const SettingsModal = defineAsyncComponent(() => import('@/components/modals/SettingsModal.vue'))
const PhonebookEdit = defineAsyncComponent(() => import('@/components/phonebook/PhonebookEdit.vue'))
const RecordingsModal = defineAsyncComponent(() => import('@/components/modals/RecordingsModal.vue'))
const VoicemailsModal = defineAsyncComponent(() => import('@/components/modals/VoicemailsModal.vue'))
const CdrModal = defineAsyncComponent(() => import('@/components/modals/CdrModal.vue'))
const PickAPhoneModal = defineAsyncComponent(() => import('./components/modals/PickAPhoneModal.vue'))
const DashboardAutoCreateModal = defineAsyncComponent(() => import('./components/modals/DashboardAutoCreateModal.vue'))
const CdrNoteEditorModal = defineAsyncComponent(() => import('./components/modals/CdrNoteEditorModal.vue'))
const CalendarModal = defineAsyncComponent(() => import('@/components/modals/CalendarModal.vue'))
const CalendarEventModal = defineAsyncComponent(() => import('@/components/modals/CalendarEventModal.vue'))

const ss = useSettingsStore()
const employees = useEmployeesStore()
const realtime = useRealtimeStore()
const dashboards = useDashboardsStore()
const { t } = useI18n()
const setPresence = ref<InstanceType<typeof SetPresenceModal>>()

function preventContextMenu(event: MouseEvent) {
	if (!['localhost', '127.0.0.1'].includes(window.location.hostname)) {
		event.preventDefault() // Prevent browser contextmenu from showing up in production
	}
}

// To enable audio playback when phone is ringing
function unlockAudioContext(audioCtx: AudioContext) {
	if (audioCtx.state !== 'suspended') {
		logger.info(`Audio context does not need unlocking, state is ${audioCtx.state}`)
		return
	}

	logger.info(`'Audio context needs unlocking, state is ${audioCtx.state}`)
	const b = document.body
	const events = ['touchstart', 'touchend', 'mousedown', 'keydown']

	events.forEach(evnt => b.addEventListener(evnt, unlock, false))

	function unlock() { audioCtx.resume().then(() => {
		clean()
		logger.info('Audio context has been unlocked')
		if (ss.settings.softphoneId) {
			DesktopNotification.show(ss.runtime.appName, t('app.ringtoneEnabled'))
		}
	}) }
	function clean() { events.forEach(evnt => b.removeEventListener(evnt, unlock)) }
}

const showLogin = computed(() => { return !ss.isAuthenticated && !ss.token })

onMounted(async () => {
	await ss.ready()
	const audioCtx = new window.AudioContext()

	unlockAudioContext(audioCtx)
	if (!ss.runtime.isEmbedded && audioCtx.state === 'suspended') {
		// Show popup for user to click so that we can have ringtone playback
		globalEvents.emit('CommonErrorModal', { header: ss.runtime.appName, message: t('app.enabledAudioByClicking') })
	}
})

// Enable notifications in the browser
if ('Notification' in window) {
	Notification.requestPermission().catch(() => { /** Ignore */ })
}

globalEvents.on('CommonSetPresenceModal', (employee: Employee) => { setPresence.value?.show(employee) })

document.addEventListener('keydown', event => {
	if (event.key === '1' && event.ctrlKey) {
		event.preventDefault()
		globalEvents.emit('MyPresenceToggleMenu')
		return
	}
	if (event.key === 's' && event.ctrlKey) {
		event.preventDefault()
		globalEvents.emit('FocusSearch')
		return
	}
	if (event.key === 'b' && event.ctrlKey) { // Send SMS
		event.preventDefault()
		globalEvents.emit('CommonSendSmsModal', { employee: undefined, number: '' })
		return
	}
	if (event.key === 'B' && event.ctrlKey && event.shiftKey) { // Send Group SMS
		event.preventDefault()
		globalEvents.emit('CommonSendGroupSmsModal')
		return
	}
	if (['2', '3', '4', '5', '6', '7', '8', '9'].includes(event.key) && event.ctrlKey) { // Topbar buttons
		event.preventDefault()
		globalEvents.emit('SelectTopbarButton', Number(event.key) - 2)
		return
	}

	if (event.key === 'k' && event.ctrlKey && ss.auth?.customerIntegrations?.ms365?.enabled) { // Calendar
		event.preventDefault()
		globalEvents.emit('CommonCalendarModal', employees.me as Employee)
		return
	}

	if (event.key === 'o' && event.ctrlKey && ss.auth?.recordingAccess) { // Recordings
		event.preventDefault()
		ss.recordingsModalVisible = !ss.recordingsModalVisible
		return
	}

	if (event.key === 't' && event.ctrlKey) { // Voicemails
		event.preventDefault()
		ss.voicemailModalVisible = !ss.voicemailModalVisible
		return
	}

	if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA' && !event.repeat) {
		// Handle sending DTMF tones
		if (employees.me?.currentCall && !event.altKey && !event.ctrlKey && !event.metaKey) {
			if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '#', '*'].includes(event.key)) {
				logger.info(`App: User pressed DTMF TONE ${event.key}`)
				globalEvents.emit('MyCallsSendDTMF', event.key)
			} else if (event.code === 'NumpadSubtract' || event.code === 'NumpadAdd') {
				logger.info('App: User pressed DTMF TONE #')
				globalEvents.emit('MyCallsSendDTMF', '#')
			}
		}
	}
})

</script>

<style scoped>
	.background {
		position: absolute !important;
		left: 50%;
		top: 50%;
		-webkit-transform: translate(-50%, -50%);
		transform: translate(-50%, -50%);
		padding-top: 120px;

	}

	.background .logo {
		font-size: 11vw;
		text-align: center;
		font-weight: 700;
		color: var(--dash-logo-color);
		text-shadow: 0 1px var(--dash-logo-color), -1px 0 var(--dash-logo-shadow),
			-1px 2px var(--dash-logo-color), -2px 1px var(--dash-logo-shadow),
			-2px 3px var(--dash-logo-color), -3px 2px var(--dash-logo-shadow),
			-3px 4px var(--dash-logo-color), -4px 3px var(--dash-logo-shadow),
			-4px 5px var(--dash-logo-color), -5px 4px var(--dash-logo-shadow),
			-5px 6px var(--dash-logo-color), -6px 5px var(--dash-logo-shadow),
			-6px 7px var(--dash-logo-color), -7px 6px var(--dash-logo-shadow),
			-7px 8px var(--dash-logo-color), -8px 7px var(--dash-logo-shadow);
		transition: 1s;
	}

	.background .loading {
		font-size: 5vw;
		text-align: center;
		color: var(--dash-logo-color);
		transition: 1s;
	}

	.disconnected-alert {
		background: var(--disconnected-alert-background);
		height: 0px;
		color: var(--disconnected-alert-color);
		font-weight: 600;
		letter-spacing: 5px;
		font-size: 20px;
		animation: flashanim 1.5s;
		animation-iteration-count: infinite;
		transition: 500ms;
		position: fixed;
		left: 0;
		right: 0;
		z-index: 1000;
		box-shadow: 0px 10px 26px 0px rgba(0, 0, 0, 0.38);
		text-align: center;
		/* padding-top: 23px; */
		overflow: hidden;
	}

	.disconnected-alert.visible {
		height: 75px;
		padding-top: 23px;
		transition: 500ms 1s;
	}

	@media (max-width: 849px) {
		.disconnected-alert.visible {
			height: 200px;
			padding-top: 100px;
			letter-spacing: 1px;
		}
	}

	@keyframes flashanim {
		0% {
			color: var(--disconnected-alert-color);
		}

		40% {
			color: var(--disconnected-alert-background);
		}

		70% {
			color: var(--disconnected-alert-color);
		}
	}

	.main-top-offset {
		padding-top: 74px;
	}

	.main-top-offset DIV {
		position: relative;
	}

	@media (max-width: 849px) {
		.main-top-offset {
			padding-top: 200px;
		}
	}
</style>
